import { useState } from "react";
import {
  Shield,
  Users,
  Zap,
  Award,
  Plus,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import ContactSection from "../components/ContactSection";

export default function About() {
  const { t } = useTranslation();

  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const toggle = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const values = [
    {
      title: t("about.values.integrity"),
      icon: Shield,
      text: t("about.values.text1"),
    },
    {
      title: t("about.values.collaboration"),
      icon: Users,
      text: t("about.values.text2"),
    },
    {
      title: t("about.values.innovation"),
      icon: Zap,
      text: t("about.values.text3"),
    },
    {
      title: t("about.values.excellence"),
      icon: Award,
      text: t("about.values.text4"),
    },
  ];

  const faqs = t("about.faq.items", {
    returnObjects: true,
  }) as {
    title: string;
    content: string;
  }[];

  return (
    <section className="max-w-[1450px] mx-auto space-y-12 sm:space-y-16 px-4 sm:px-6 lg:px-8 overflow-hidden">

      {/* HERO */}
      <div className="flex flex-col lg:flex-row items-start lg:items-center gap-10 lg:gap-20 py-4 lg:py-10">

    <div className="space-y-6 w-full">
      <h2 className="text-[3.5rem] font-black leading-[1] text-slate-900 break-words w-full">
      {t("about.hero.title1")}{" "}
      {t("about.hero.title2")}
    </h2>

    <p className="text-base sm:text-lg text-slate-500 leading-relaxed w-full">
      {t("about.hero.subtitle")}
    </p>

  </div>
</div>

      {/* MINI SLIDER */}
      <div className="max-w-[1450px] rounded-3xl bg-slate-50 px-6 sm:px-10 py-8 sm:py-10 text-center">
        <p className="text-base sm:text-xl font-bold uppercase tracking-[0.2em] text-blue-600 leading-relaxed">
          {t("about.mini")}
        </p>
      </div>

      {/* MISSION */}
      <div className="max-w-[1450px] grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">

        {/* LEFT */}
        <div className="space-y-6">

          <h2 className="text-xl sm:text-xl lg:text-xl leading-tight text-slate-900">
            {t("about.mission.title")}
          </h2>

          <p className="text-base sm:text-lg text-slate-500 leading-relaxed">
            {t("about.mission.text")}
          </p>

        </div>

        {/* RIGHT */}
        <div className="overflow-hidden rounded-[28px] sm:rounded-[40px] shadow-xl">
          <img
            src="https://images.unsplash.com/photo-1551434678-e076c223a692?q=80&w=800"
            className="w-full h-[260px] sm:h-[420px] object-cover"
          />
        </div>

      </div>

      {/* VALUES */}
      <div className="max-w-[1450px] space-y-8 sm:space-y-10">

        <h3 className="text-3xl sm:text-4xl font-black text-center uppercase tracking-wide leading-tight">
          {t("about.values.title")}
        </h3>

        <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-5 sm:gap-6">

          {values.map((item, i) => {
            const Icon = item.icon;

            return (
              <div
                key={i}
                className="rounded-2xl border border-slate-100 bg-white p-6 sm:p-8 flex flex-col items-center text-center transition hover:-translate-y-1 hover:shadow-lg"
              >

                <Icon className="mb-5 h-7 w-7 text-blue-600" />

                <p className="mb-3 text-sm sm:text-base font-black uppercase tracking-widest leading-relaxed">
                  {item.title}
                </p>

                <p className="text-sm sm:text-base leading-relaxed text-slate-500">
                  {item.text}
                </p>

              </div>
            );
          })}

        </div>
      </div>

      {/* FAQ */}
      <div className="mx-auto space-y-6 sm:space-y-8">

        <h3 className="text-3xl sm:text-4xl font-black text-center uppercase tracking-wide leading-tight">
          {t("about.faq.title")}
        </h3>

        {faqs.map((item, i) => (
          <div
            key={i}
            onClick={() => toggle(i)}
            className="rounded-2xl border p-5 sm:p-8 cursor-pointer transition hover:border-blue-500 hover:shadow-sm"
          >

            <div className="flex items-start justify-between gap-4">

              <h4 className="text-sm sm:text-base uppercase tracking-widest leading-relaxed">
                {item.title}
              </h4>

              <Plus
                className={`min-w-[20px] min-h-[20px] transition duration-300
                  ${
                    openIndex === i
                      ? "rotate-45 text-red-500"
                      : "text-slate-400"
                  }
                `}
              />
            </div>

            <div
              className={`overflow-hidden transition-all duration-300
                ${
                  openIndex === i
                    ? "max-h-[500px] opacity-100 mt-5"
                    : "max-h-0 opacity-0"
                }
              `}
            >
              <p className="text-sm sm:text-base leading-relaxed text-slate-500">
                {item.content}
              </p>
            </div>

          </div>
        ))}

      </div>

      <ContactSection />
    </section>
  );
}